import React, {useState, useEffect} from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {useAuth} from '../contexts/Auth0Context.jsx';
import apiService from '../services/apiService';
import CampaignSetup, { DRAFT_STORAGE_KEY as CAMPAIGN_DRAFT_KEY } from './CampaignSetup.jsx';
import PlayerSessionModal from './PlayerSessionModal.jsx';
import RegistrationFlow from './RegistrationFlow.jsx';
import background from '../assets/background.webp';
import logo from '../assets/logo.webp';
import SharedHeaderLayout from './layout/SharedHeaderLayout.jsx';
import {API_CONFIG, apiUrl} from '../config/api.js';
import ScrollPanel, { ScrollButton } from './ScrollPanel.jsx';
import { buildPrimaryCampaignRoute } from '../utils/campaignRoutes.js';
import useMobileBreakpoint from '../hooks/useMobileBreakpoint';
import './WelcomePage.css';

const WelcomePage = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // No useScopedMusic here — MusicProvider's title_theme fallback covers /welcome.

    // Mobile breakpoint — controls mobile-conditional UI branches
    const isMobile = useMobileBreakpoint();

    // Auth context
    const {isAuthenticated, loading, user, login, getAccessTokenSilently} = useAuth();
    const isAdmin = user?.is_admin ?? false;

    // User status from /api/user/me
    const [userStatus, setUserStatus] = useState(null);
    const [showWelcomePopup, setShowWelcomePopup] = useState(false);

    // Derived menu tier state. Admins skip the tutorial and should always see the post-tutorial menu.
    const tutorialCompleted = isAdmin || (userStatus?.tutorial_completed ?? false);

    // Backend readiness state
    const [backendReady, setBackendReady] = useState(false);
    const [backendError, setBackendError] = useState(null);
    const [checkingBackend, setCheckingBackend] = useState(true);

    // Modal state management
    const [showCampaignSetup, setShowCampaignSetup] = useState(false);
    const [forceSinglePlayer, setForceSinglePlayer] = useState(false);
    const [showPlayerSessionModal, setShowPlayerSessionModal] = useState(false);

    // Invite token processing state
    const [processingInvite, setProcessingInvite] = useState(false);
    const [inviteError, setInviteError] = useState(null);
    const [tokenProviderReady, setTokenProviderReady] = useState(false);

    // Registration status tracking
    const [checkingAuthorization, setCheckingAuthorization] = useState(false);
    const [showRegistration, setShowRegistration] = useState(false);
    const [prefillCharacterId, setPrefillCharacterId] = useState(null); // set from ?prefill_character_id= URL param

    // Arena quick start loading state
    const [startingArena, setStartingArena] = useState(false);

    // Backend recovery controls (admin-only)
    const [requestingBackendRecovery, setRequestingBackendRecovery] = useState(false);
    const [backendRecoveryNotice, setBackendRecoveryNotice] = useState(null);

    // Most-recently-played player campaign for the "Continue Adventure" shortcut.
    // null = not fetched yet, fetch failed, or user has no player-role campaigns.
    const [continueCampaign, setContinueCampaign] = useState(null);

    // Fetch user status for menu tier logic (replaces separate billing check)
    useEffect(() => {
        const fetchUserStatus = async () => {
            if (!isAuthenticated || loading) {
                setUserStatus(null);
                return;
            }

            try {
                const token = await getAccessTokenSilently();
                const response = await fetch(apiUrl('/api/user/me'), {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setUserStatus(data);

                    // Check if we should show welcome popup (from URL param)
                    const isSubscribed = data.has_subscription &&
                        data.subscription_status &&
                        ['active', 'trialing'].includes(data.subscription_status);
                    const showWelcome = searchParams.get('show_welcome') === '1';
                    if (isSubscribed && showWelcome) {
                        setShowWelcomePopup(true);
                        searchParams.delete('show_welcome');
                        setSearchParams(searchParams, { replace: true });
                    }
                }
            } catch (err) {
                console.error('Error fetching user status:', err);
            }
        };

        fetchUserStatus();
    }, [isAuthenticated, loading, getAccessTokenSilently, searchParams, setSearchParams]);

    // Check backend health on mount with retry logic
    useEffect(() => {
        let isMounted = true;
        let retryCount = 0;
        const maxRetries = 10;
        const retryDelay = 2000; // 2 seconds between retries

        const checkBackendHealth = async () => {
            try {
                const response = await fetch(API_CONFIG.HEALTH_ENDPOINT, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    if (isMounted && data.status === 'healthy') {
                        console.log('Backend is ready');
                        setBackendReady(true);
                        setBackendError(null);
                        setCheckingBackend(false);
                        return true;
                    }
                }
                return false;
            } catch (error) {
                console.log(`Backend health check failed (attempt ${retryCount + 1}/${maxRetries}):`, error.message);
                return false;
            }
        };

        const attemptHealthCheck = async () => {
            const isHealthy = await checkBackendHealth();

            if (!isHealthy && isMounted) {
                retryCount++;
                if (retryCount < maxRetries) {
                    // Retry after delay
                    setTimeout(attemptHealthCheck, retryDelay);
                } else {
                    // Max retries exceeded
                    setBackendError('Unable to connect to server. Please refresh the page to try again.');
                    setCheckingBackend(false);
                }
            }
        };

        attemptHealthCheck();

        return () => {
            isMounted = false;
        };
    }, []);

    // Set up apiService token provider when authenticated
    useEffect(() => {
        if (getAccessTokenSilently && !apiService.getAccessToken) {
            apiService.setTokenProvider(async () => {
                return await getAccessTokenSilently();
            });
            setTokenProviderReady(true);
        } else if (apiService.getAccessToken) {
            // Already set by another component
            setTokenProviderReady(true);
        }
    }, [getAccessTokenSilently]);

    // Fetch the player's most recently played campaign so we can offer a
    // one-click "Continue Adventure" shortcut. Hides the button silently
    // if the user has no player-role campaigns or the fetch fails.
    useEffect(() => {
        if (!isAuthenticated || loading || !tokenProviderReady) {
            return;
        }

        let cancelled = false;
        const loadRecentCampaign = async () => {
            try {
                const result = await apiService.listSimpleCampaigns({
                    role: 'player',
                    limit: 1,
                });
                const recent = result?.campaigns?.[0] ?? null;
                if (!cancelled) {
                    setContinueCampaign(recent);
                }
            } catch (error) {
                console.error('Failed to load most-recent campaign for Continue Adventure:', error);
                if (!cancelled) {
                    setContinueCampaign(null);
                }
            }
        };

        loadRecentCampaign();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, loading, tokenProviderReady]);

    // Handle invite token on mount
    useEffect(() => {
        const inviteToken = searchParams.get('invite');
        if (!inviteToken) return;

        // Wait for auth state to be determined
        if (loading) return;

        // If not authenticated, redirect to login with invite token preserved
        if (!isAuthenticated) {
            console.log('🔐 User not authenticated, redirecting to login with invite token');
            login({
                appState: {
                    returnTo: `/?invite=${encodeURIComponent(inviteToken)}`
                }
            });
            return;
        }

        // Wait for token provider to be ready before processing invite
        if (!tokenProviderReady) {
            console.log('🔐 Waiting for token provider to be ready...');
            return;
        }

        // User is authenticated, process the invite
        handleInviteToken(inviteToken);
    }, [searchParams, loading, isAuthenticated, login, tokenProviderReady]);

    // Re-open campaign setup when returning from /characters/new?from=campaign-setup.
    // Draft is preserved in localStorage (DRAFT_STORAGE_KEY) so the wizard resumes
    // at the step the user left. setupMode restores whether the draft came from
    // solo or multi-player setup; missing values keep the original solo behavior.
    useEffect(() => {
        if (!isAuthenticated || loading) return;
        const openSetup = searchParams.get('openCampaignSetup');
        if (openSetup !== '1') return;
        const setupMode = searchParams.get('setupMode');
        const nextSearchParams = new URLSearchParams(searchParams);
        nextSearchParams.delete('openCampaignSetup');
        nextSearchParams.delete('setupMode');
        setSearchParams(nextSearchParams, { replace: true });
        setForceSinglePlayer(setupMode !== 'campaign');
        setShowCampaignSetup(true);
    }, [isAuthenticated, loading, searchParams, setSearchParams]);

    // Handle intent after login (check URL params)
    useEffect(() => {
        const handleIntent = async () => {
            const intent = searchParams.get('intent');
            const prefillId = searchParams.get('prefill_character_id');
            const openSetup = searchParams.get('open_setup') === '1';

            if (intent && isAuthenticated && !loading) {
                // Clear intent from URL
                searchParams.delete('intent');
                searchParams.delete('prefill_character_id');
                searchParams.delete('open_setup');
                setSearchParams(searchParams, {replace: true});

                // Check authorization before opening modal
                const authorized = await checkUserAuthorization();
                if (!authorized) {
                    return;
                }

                // Open the intended modal or start quick-start flows
                if (intent === 'player') {
                    if (prefillId) {
                        // Go directly to campaign setup wizard with character prefilled
                        setPrefillCharacterId(prefillId);
                        setForceSinglePlayer(true);
                        setShowCampaignSetup(true);
                    } else if (openSetup) {
                        localStorage.removeItem(CAMPAIGN_DRAFT_KEY);
                        setForceSinglePlayer(true);
                        setShowCampaignSetup(true);
                    } else {
                        setShowPlayerSessionModal(true);
                    }
                } else if (intent === 'tutorial') {
                    // Start tutorial quick-start flow
                    try {
                        const response = await apiService.createTutorialQuickStart();
                        const routeId = response?.external_campaign_id || response?.campaign_id;
                        if (routeId) {
                            navigate(`/${routeId}/player`, { state: { isTutorial: true } });
                        }
                    } catch (error) {
                        console.error('Failed to start tutorial:', error);
                    }
                } else if (intent === 'arena') {
                    // Start arena quick-start flow
                    setStartingArena(true);
                    try {
                        const response = await apiService.createArenaQuickStart();
                        const routeId = response?.external_campaign_id || response?.campaign_id;
                        if (routeId) {
                            navigate(`/${routeId}/combat`, { state: { isArena: true } });
                        }
                    } catch (error) {
                        console.error('Failed to start arena:', error);
                        setStartingArena(false);
                    }
                }
            }
        };

        handleIntent();
    }, [searchParams, isAuthenticated, loading]);

    // Process invite token and auto-join session
    const handleInviteToken = async (token) => {
        try {
            setProcessingInvite(true);
            setInviteError(null);

            console.log('🎟️ Processing invite token:', token);

            const response = await apiService.joinSessionByInvite(token);
            const sessionId = response?.external_campaign_id || response?.session_id || response?.campaign_id;

            if (!sessionId) {
                throw new Error('Invite did not return a session ID');
            }

            console.log('✅ Joined session via invite:', sessionId);

            // Remove invite token from URL
            searchParams.delete('invite');
            setSearchParams(searchParams, {replace: true});

            // Navigate to player view for this session
            navigate(`/${sessionId}/player`, {replace: true});
        } catch (error) {
            console.error('❌ Failed to process invite:', error);
            setInviteError(error.message || 'Invalid or expired invite link');
            setProcessingInvite(false);

            // Remove invite token from URL even on error
            searchParams.delete('invite');
            setSearchParams(searchParams, {replace: true});
        }
    };

    // Check if user is authorized (whitelist check)
    const checkUserAuthorization = async () => {
        if (!user) return false;

        setCheckingAuthorization(true);

        try {
            const response = await fetch(apiUrl('/api/auth/registration-status'), {
                headers: {
                    Authorization: `Bearer ${await getAccessTokenSilently()}`,
                },
            });

            if (response.ok) {
                const status = await response.json();

                // If pending or not authorized, show registration flow
                if (status.registration_status === 'pending' || !status.is_authorized) {
                    setShowRegistration(true);
                    return false;
                }

                return true; // Authorized
            } else if (response.status === 403) {
                setShowRegistration(true);
                return false;
            }

            // If we can't check, assume authorized (fallback)
            return true;
        } catch (error) {
            console.error('Error checking authorization:', error);
            return true; // Assume authorized on error
        } finally {
            setCheckingAuthorization(false);
        }
    };

    // Handler for "Adventurer" button
    const handleAdventurerClick = () => {
        navigate('/adventure');
    };

    // Handler for the "Continue Adventure" shortcut. Navigates to the primary
    // route for the most recently played campaign (player for quest_loop, dm
    // otherwise — matches the existing card-click flow on /adventure).
    const handleContinueAdventureClick = () => {
        if (!continueCampaign) {
            return;
        }
        const routeId = continueCampaign.external_campaign_id || continueCampaign.campaign_id;
        const target = buildPrimaryCampaignRoute(routeId, continueCampaign.campaign_type);
        if (target) {
            navigate(target);
        }
    };

    const handleAdventurerStartNewCampaign = () => {
        localStorage.removeItem(CAMPAIGN_DRAFT_KEY);
        setPrefillCharacterId(null);
        setForceSinglePlayer(true);
        setShowPlayerSessionModal(false);
        setShowCampaignSetup(true);
    };

    // Handler for when campaign setup wizard completes
    const handleCampaignSetupComplete = (campaignId) => {
        setShowCampaignSetup(false);
        if (campaignId) {
            if (forceSinglePlayer) {
                navigate(`/${campaignId}/player`, { state: { singlePlayerStart: true } });
            } else {
                navigate(`/${campaignId}/dm`);
            }
        }
        setForceSinglePlayer(false);
    };

    const handleBackendRecovery = async () => {
        if (requestingBackendRecovery) {
            return;
        }

        setRequestingBackendRecovery(true);
        setBackendRecoveryNotice(null);

        try {
            await apiService.requestBackendRestart({
                reason: 'welcome_page_connection_failed',
                delaySeconds: 2,
            });

            setBackendRecoveryNotice({
                type: 'success',
                message: 'Backend restart requested. Retrying connection in 10 seconds...',
            });

            setTimeout(() => {
                window.location.reload();
            }, 10000);
        } catch (error) {
            console.error('Failed to request backend recovery:', error);
            setBackendRecoveryNotice({
                type: 'error',
                message: error?.message || 'Restart request failed. Confirm you are signed in as an admin.',
            });
        } finally {
            setRequestingBackendRecovery(false);
        }
    };

    // Mobile CTA label: returning users see "Play Now", guests see "Sign In"
    const mobileCTALabel = isAuthenticated ? 'Play Now' : 'Sign In';

    const handleMobileCTA = () => {
        if (isAuthenticated) {
            // Authenticated: go directly to the adventure landing
            navigate('/adventure');
        } else {
            login({
                appState: {
                    returnTo: window.location.pathname + (window.location.search || ''),
                },
            });
        }
    };

    return (
        <div className="min-h-screen bg-gaia-bg flex flex-col">
            {/* On mobile, hide the standalone Login button — the consolidated CTA below takes over */}
            <SharedHeaderLayout hideLoginOnMobile={isMobile}/>
            <div
                className="flex-grow flex flex-col items-center justify-center relative overflow-hidden"
                style={{
                    backgroundImage: `url(${background})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                }}
            >
                {/* Dark overlay for better text readability */}
                <div className="absolute inset-0 bg-black/20"></div>

                {/* Backend error overlay */}
                {backendError && !checkingBackend && (
                    <div className="absolute inset-0 bg-black/70 z-20 flex flex-col items-center justify-center">
                        <div className="text-center max-w-md px-4">
                            <div className="flex justify-center mb-6">
                                <img
                                    src={logo}
                                    alt="Fable Table"
                                    className="w-32 h-32 md:w-40 md:h-40 object-contain drop-shadow-2xl opacity-50"
                                />
                            </div>
                            <p
                                className="text-xl text-red-300 mb-4 drop-shadow-lg"
                                style={{
                                    fontFamily: 'var(--ft-font-display)',
                                    textShadow: '0 0 10px rgba(0,0,0,0.9)'
                                }}
                            >
                                Connection Failed
                            </p>
                            <p className="text-gray-300 mb-4">{backendError}</p>
                            {backendRecoveryNotice && (
                                <p
                                    className={`mb-4 text-sm ${backendRecoveryNotice.type === 'error' ? 'text-red-300' : 'text-emerald-300'}`}
                                >
                                    {backendRecoveryNotice.message}
                                </p>
                            )}
                            <div className="flex flex-col gap-3">
                                <button
                                    onClick={() => window.location.reload()}
                                    className="px-6 py-3 bg-amber-700 hover:bg-amber-600 text-white rounded-lg transition-colors duration-200 font-semibold"
                                >
                                    Retry Connection
                                </button>
                                {isAdmin && (
                                    <button
                                        onClick={handleBackendRecovery}
                                        disabled={requestingBackendRecovery}
                                        className="px-6 py-3 bg-red-800 hover:bg-red-700 disabled:bg-red-900/60 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200 font-semibold"
                                    >
                                        {requestingBackendRecovery ? 'Requesting Restart...' : 'Request Backend Restart'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content */}
                {!backendError && <main className="relative z-10 flex flex-col items-center justify-center px-4 py-8 w-full">
                    {/* Logo above scroll */}
                    <div className="flex justify-center mb-4" style={{marginTop: '-60px'}}>
                        <img
                            src={logo}
                            alt="Fable Table"
                            className="w-36 h-36 md:w-44 md:h-44 object-contain drop-shadow-2xl"
                        />
                    </div>

                    {/* Invite Processing Status - shown above scroll */}
                    {processingInvite && (
                        <div
                            className="bg-blue-900/90 backdrop-blur-sm border border-blue-700 text-blue-100 px-6 py-4 rounded-lg mb-4 shadow-2xl">
                            <div className="flex items-center justify-center gap-3">
                                <div
                                    className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-blue-300"></div>
                                <span className="text-lg">Processing invite link...</span>
                            </div>
                        </div>
                    )}

                    {/* Invite Error - shown above scroll */}
                    {inviteError && (
                        <div
                            className="bg-red-900/90 backdrop-blur-sm border border-red-700 text-red-100 px-6 py-4 rounded-lg mb-4 shadow-2xl">
                            <p className="font-semibold mb-2 text-lg">Invite Error</p>
                            <p className="text-sm">{inviteError}</p>
                            <button
                                onClick={() => setInviteError(null)}
                                className="mt-3 text-sm underline hover:text-red-200 transition-colors"
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    {/* Scroll Panel with all navigation */}
                    <ScrollPanel
                        title="Choose Your Journey"
                    >
                        {tutorialCompleted ? (
                            /* Post-tutorial menu */
                            <>
                                {continueCampaign && (
                                    <ScrollButton
                                        onClick={handleContinueAdventureClick}
                                        disabled={!backendReady}
                                        variant="primary"
                                    >
                                        Continue Adventure
                                        <span className="block text-sm opacity-80 mt-1">
                                            {continueCampaign.name || 'Pick up where you left off'}
                                        </span>
                                    </ScrollButton>
                                )}

                                <ScrollButton
                                    onClick={handleAdventurerClick}
                                    variant="secondary"
                                >
                                    Adventurer
                                </ScrollButton>

                                <ScrollButton
                                    as={Link}
                                    to="/characters"
                                >
                                    Characters
                                </ScrollButton>
                            </>
                        ) : (
                            /* Tier 1: New or unauthenticated user — just Play Now */
                            <ScrollButton
                                as={Link}
                                to="/play-now?fresh=1"
                                variant="success"
                            >
                                Play Now
                            </ScrollButton>
                        )}
                    </ScrollPanel>

                    {/* Mobile-only: consolidated CTA — "Sign In" for guests,
                        "Play Now" for authenticated/returning players (label chosen
                        by mobileCTALabel). Replaces the hidden nav Login button on
                        mobile and serves as the primary mobile CTA in the post-tutorial
                        menu. Hidden on desktop via CSS (.welcome-mobile-cta display:none above 768px) */}
                    {isMobile && (
                        <button
                            type="button"
                            data-testid="mobile-cta-btn"
                            className="welcome-mobile-cta"
                            onClick={handleMobileCTA}
                        >
                            {mobileCTALabel}
                        </button>
                    )}

                    {/* Discord Link - below scroll */}
                    <a
                        href="https://discord.gg/WCZwgJbSqZ"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 mt-6 text-[#5865F2] hover:text-[#7289DA] transition-colors duration-200 drop-shadow-lg"
                        title="Discord"
                    >
                        <svg
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-5 h-5"
                        >
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z"/>
                        </svg>
                        <span
                            className="text-sm tracking-wide"
                            style={{
                                fontFamily: 'var(--ft-font-display)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em'
                            }}
                        >
                            Join our Discord
                        </span>
                    </a>

                    {/* Blog link — full-page navigation to the prerendered static blog at /blog */}
                    <a
                        href="/blog/"
                        className="inline-flex items-center gap-2 mt-4 text-[#b88422] hover:opacity-80 transition-opacity duration-200 drop-shadow-lg"
                        title="Blog"
                    >
                        <span
                            className="text-sm tracking-wide"
                            style={{
                                fontFamily: 'var(--ft-font-display)',
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em'
                            }}
                        >
                            Blog
                        </span>
                    </a>
                </main>}

                {/* ElevenLabs Grant Logo — welcome-elevenlabs-footer adds flex-wrap
                    and max-width:100% at mobile viewports to prevent overflow */}
                <div className="absolute bottom-4 right-4 z-10 welcome-elevenlabs-footer">
                    <a
                        href="https://elevenlabs.io/startup-grants"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="opacity-80 hover:opacity-100 transition-opacity duration-200"
                    >
                        <img
                            src="https://eleven-public-cdn.elevenlabs.io/payloadcms/cy7rxce8uki-IIElevenLabsGrants%201.webp"
                            alt="ElevenLabs"
                            style={{ width: '250px' }}
                        />
                    </a>
                </div>
            </div>

            {/* Campaign Setup Wizard Modal */}
            <CampaignSetup
                isOpen={showCampaignSetup}
                onCancel={() => {
                    setShowCampaignSetup(false);
                    setForceSinglePlayer(false);
                }}
                onComplete={handleCampaignSetupComplete}
                prefillCharacterId={prefillCharacterId}
                forceSinglePlayer={forceSinglePlayer}
            />

            {/* Player Session Modal */}
            {showPlayerSessionModal && (
                <PlayerSessionModal
                    isOpen={showPlayerSessionModal}
                    onClose={() => setShowPlayerSessionModal(false)}
                    onStartNewCampaign={handleAdventurerStartNewCampaign}
                />
            )}

            {/* Loading state while checking authorization */}
            {checkingAuthorization && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-gaia-surface rounded-lg p-6">
                        <div
                            className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gaia-primary mx-auto"></div>
                        <p className="text-white mt-4">Checking access...</p>
                    </div>
                </div>
            )}

            {/* Loading state while starting arena */}
            {startingArena && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div className="text-center">
                        <div className="flex justify-center mb-6">
                            <img
                                src={logo}
                                alt="Fable Table"
                                className="w-32 h-32 md:w-40 md:h-40 object-contain drop-shadow-2xl animate-pulse"
                            />
                        </div>
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-red-400 mx-auto mb-4"></div>
                        <p
                            className="text-xl text-gray-200 drop-shadow-lg"
                            style={{
                                fontFamily: 'var(--ft-font-display)',
                                textShadow: '0 0 10px rgba(0,0,0,0.9)'
                            }}
                        >
                            Entering the Arena...
                        </p>
                    </div>
                </div>
            )}

            {/* Show registration flow if needed */}
            {showRegistration && (
                <RegistrationFlow
                    onComplete={() => {
                        setShowRegistration(false);
                        // After registration, they can try the action again
                    }}
                />
            )}

            {/* Adventure Mode Welcome Popup */}
            {showWelcomePopup && (
                <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
                    <div
                        className="bg-gradient-to-b from-amber-900/95 to-amber-950/95 rounded-xl p-8 max-w-md mx-4 border border-amber-600/50 shadow-2xl"
                        style={{
                            boxShadow: '0 0 40px rgba(218, 165, 32, 0.3), 0 0 80px rgba(122, 78, 20, 0.2)'
                        }}
                    >
                        <div className="text-center">
                            <div className="flex justify-center mb-4">
                                <img
                                    src={logo}
                                    alt="Fable Table"
                                    className="w-24 h-24 object-contain drop-shadow-2xl"
                                />
                            </div>
                            <h2
                                className="text-2xl md:text-3xl font-bold text-amber-200 mb-4"
                                style={{
                                    fontFamily: 'var(--ft-font-display)',
                                    textShadow: '0 2px 4px rgba(0,0,0,0.5)'
                                }}
                            >
                                Welcome!
                            </h2>
                            <p className="text-gray-200 mb-6 leading-relaxed">
                                Adventure awaits — start a new quest or pick up where you left off.
                            </p>
                            <button
                                onClick={async () => {
                                    setShowWelcomePopup(false);
                                    // Mark as shown on the backend
                                    try {
                                        const token = await getAccessTokenSilently();
                                        await fetch(apiUrl('/api/billing/mark-adventure-welcome-shown'), {
                                            method: 'POST',
                                            headers: {
                                                Authorization: `Bearer ${token}`,
                                            },
                                        });
                                    } catch (err) {
                                        console.error('Error marking welcome as shown:', err);
                                    }
                                }}
                                className="px-8 py-3 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white rounded-lg transition-all duration-200 font-semibold shadow-lg hover:shadow-xl"
                                style={{
                                    fontFamily: 'var(--ft-font-display)'
                                }}
                            >
                                Let's Go!
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WelcomePage;
